import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { adminService } from '../services/admin';
import { AdminStatistics } from '../types/admin';
import { AdminDashboardViewModel } from '../types/dashboard';
import { useAuth } from './useAuth';
import { isAdminUser } from '../types/auth';
import { buildAdminDashboardViewModel } from '../utils/adminDashboardViewModel';

export const useAdminDashboard = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState<AdminStatistics | null>(null);
  const [viewModel, setViewModel] = useState<AdminDashboardViewModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!user || !isAdminUser(user)) {
      setLoading(false);
      setViewModel(null);
      setStats(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const nextStats = await adminService.getStatistics();
      setStats(nextStats);
    } catch (err) {
      console.error('Error loading admin dashboard:', err);
      setError(i18n.t('dashboard.errors.loadFailed'));
      setViewModel(null);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [user, i18n]);

  useEffect(() => {
    let cancelled = false;
    const userId = user?.id;
    const canLoad = !!user && isAdminUser(user);

    const run = async () => {
      if (!userId || !canLoad) {
        setLoading(false);
        setViewModel(null);
        setStats(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const nextStats = await adminService.getStatistics();
        if (!cancelled) {
          setStats(nextStats);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading admin dashboard:', err);
          setError(i18n.t('dashboard.errors.loadFailed'));
          setViewModel(null);
          setStats(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // Only re-fetch when the authenticated user changes — not on language/t updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!user || !stats) {
      return;
    }
    setViewModel(buildAdminDashboardViewModel(stats, user, i18n.language, t));
  }, [user, stats, i18n.language, t]);

  return {
    viewModel,
    loading,
    error,
    reload,
  };
};
